import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Button, Column, Container, Icon, Row } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs } from '../../components';
import { isValidArray } from '../../components/utilities/validations';
import { useLivePreview } from '../../hooks/use-live-preview';

import DynamicComponent from '../../components/common/dynamic-component';
import FAQ from '../../components/common/faq';
import AnchorLinks from '../../components/common/anchor-links';
import InspirationGrid from '../../components/common/inspiration-grid';
import ArticleGrid from '../../components/template/category-inspiration/article-grid';
import HoverBannerLarge from '../../components/third-party/hover/large-banner';

const CategoryInspiration = (props) => {
	const pageData = useLivePreview(props.data.contentstackCategoryInspiration, 'category_inspiration');
	const { inspirationEntries, articleEntries } = props.data;
	const isPro = pageData?.product_type?.product_type === 'PRO' ? true : false;
	const [readMore, setReadMore] = useState(false);

	const truncateSubheading = (text) => (text.length > 150 ? text.substring(0, 150) + '&hellip;' : text);

	return (
		<Layout
			id='template-CategoryInspiration'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			<Container>
				<Row className='py-5'>
					<Column
						xs={12}
						md={9}
						xl={8}
					>
						{pageData.heading && (
							<h1
								{...(pageData.$?.heading ?? {})}
								className='mb-2 mt-0'
							>
								{pageData.heading}
							</h1>
						)}

						{pageData.subheading && (
							<div
								{...(pageData.$?.subheading ?? {})}
								className='mb-4 text-pretty md:mb-2'
								dangerouslySetInnerHTML={{ __html: !readMore ? truncateSubheading(pageData.subheading) : pageData.subheading }}
							/>
						)}

						{pageData.subheading?.length > 150 && (
							<Button
								color='tertiary-alt'
								onClick={() => setReadMore(!readMore)}
								className='group !w-fit no-underline'
							>
								<span className='underline decoration-black decoration-2 underline-offset-4 group-hover:decoration-transparent group-focus:decoration-transparent'>Read {readMore ? 'Less' : 'More'}</span>
								<Icon name={readMore ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
							</Button>
						)}
					</Column>
				</Row>

				{pageData.description && (
					<h3
						{...(pageData.$?.description ?? {})}
						className='mb-0 mt-4'
					>
						{pageData.description}
					</h3>
				)}
			</Container>

			{!isPro && (
				<InspirationGrid
					productType={pageData.product_type?.product_type}
					staticTiles={inspirationEntries}
					baseFilters={pageData.filter_tags}
					filterCategory={pageData.filter_category}
				/>
			)}

			{isPro && (
				<ArticleGrid
					productType={pageData.product_type?.product_type}
					staticTiles={articleEntries}
					caseStudyType={pageData.case_study_type}
					baseFilters={pageData.filter_tags}
					filterCategory={pageData.filter_category}
				/>
			)}

			{isValidArray(pageData.components) &&
				pageData.components.map((block, index) => {
					if (block.cta_ribbon != null) {
						return (
							<div
								key={index}
								className='bg-black py-8'
								{...(pageData.$?.[`components__${index}`] ?? {})}
							>
								<Container>
									{block.cta_ribbon.heading && (
										<h2
											{...(block.cta_ribbon.$?.heading ?? {})}
											className='text-h3 mb-6 text-white'
										>
											{block.cta_ribbon.heading}
										</h2>
									)}
									{block.cta_ribbon.subheading && (
										<p
											{...block.cta_ribbon.$?.subheading}
											className='text-overline mb-2 hidden text-neutral-200 md:block'
										>
											{block.cta_ribbon.subheading}
										</p>
									)}

									{isValidArray(block.cta_ribbon.cta) && (
										<AnchorLinks
											editTags={block.cta_ribbon.$}
											data={block.cta_ribbon.cta}
											currentUrl={pageData.url}
											buttonText={block.cta_ribbon.subheading}
											className='!m-0 [&_a]:max-lg:text-white [&_li]:!p-0 [&_ul]:!mb-0 [&_ul]:!justify-start [&_ul]:gap-3'
											dark
										/>
									)}
								</Container>
							</div>
						);
					}

					if (block.dynamic != null) {
						return (
							<DynamicComponent
								key={index}
								data={block.dynamic}
								{...(pageData.$?.[`components__${index}`] ?? {})}
							/>
						);
					}

					if (block.faqs != null) {
						return (
							<div
								key={index}
								className='bg-neutral-25 py-8 xl:py-10'
								{...(pageData.$?.[`components__${index}`] ?? {})}
							>
								<Container width={890}>
									{block.faqs.heading && (
										<h2
											{...(block.faqs.$?.heading ?? {})}
											className='mb-6'
										>
											{block.faqs.heading}
										</h2>
									)}
									<FAQ data={block.faqs.faqs} />
								</Container>
							</div>
						);
					}

					if (block.embed != null) {
						return (
							<div
								key={index}
								className='catInspiration-embed my-10'
							>
								<Container>
									<HoverBannerLarge />
								</Container>
							</div>
						);
					}
				})}

			{isValidArray(pageData.all_dynamic) &&
				pageData.all_dynamic.map((item, index) => (
					<DynamicComponent
						key={index}
						data={item}
					/>
				))}
		</Layout>
	);
};

CategoryInspiration.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
	className: PropTypes.string,
};

export default CategoryInspiration;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackCategoryInspiration.seo} />;

export const query = graphql`
	query ($uid: String!, $filter_tag: [String]) {
		contentstackCategoryInspiration(uid: { eq: $uid }) {
			__typename
			uid
			url
			breadcrumbs {
				...Breadcrumbs
			}
			heading
			subheading
			description
			case_study_type
			filter_tags
			filter_category
			product_type {
				product_type
			}
			components {
				cta_ribbon {
					heading
					subheading
					cta {
						...CTA
					}
				}
				dynamic {
					background_hex_value
					heading
					dynamic {
						...Dynamic
					}
				}
				faqs {
					heading
					faqs {
						question
						answer
					}
				}
				embed {
					embed_id
				}
			}
			seo {
				...SEO
			}
		}

		inspirationEntries: allContentstackImageDetail(filter: { taxonomies: { elemMatch: { term_uid: { in: $filter_tag } } } }, sort: { created_at: DESC }, limit: 14) {
			totalCount
			nodes {
				...ImageDetail
				tiles {
					tile {
						...Tile
					}
				}
				created_at
			}
		}

		articleEntries: allContentstackArticles(filter: { taxonomies: { elemMatch: { term_uid: { in: $filter_tag } } } }, sort: { created_at: DESC }, limit: 16) {
			totalCount
			nodes {
				...Article
			}
		}
	}
`;
